/**
 * DetailPeriod — sezioni di dettaglio voci per un singolo periodo.
 *
 * Replica del componente mobile (DetailPeriod.jsx):
 *   1. Card "Riepilogo voci - <periodo>": lista voci con totale aggregato
 *      + totale periodo
 *   2. Card "Totale bolletta" (importo della bolletta intera)
 *   3. Card "Distribuzione voci" con HorizontalBarsChart
 *   4. Card "Dettaglio per voce" — una VoiceTable per ciascuna voce
 */

import {
  HorizontalBarsChart,
  type HorizontalBarItem,
} from '@/components/charts/HorizontalBarsChart'
import { Card } from '@/components/ui/card'
import type { PeriodDetail } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'
import { COLORS_PIE, EURO, VOCI_BOLLETTA } from '../constants'
import { VoiceTable } from './VoiceTable'

type Props = {
  /** Label del periodo (es. "Marzo 2026"). */
  periodLabel: string
  /** Dati del periodo: mappa { voice → items[] }. */
  period: PeriodDetail
  /** Totale bolletta (può differire dalla somma del periodo in caso di bimestrale). */
  totaleBolletta: number
}

type VoiceTotal = {
  code: string
  label: string
  total: number
  color: string
}

function buildVoiceTotals(period: PeriodDetail): VoiceTotal[] {
  return Object.keys(period).map((voice) => {
    const items = Array.isArray(period[voice]) ? period[voice] : []
    const total = items.reduce((acc, item) => acc + (Number(item?.imponibile) || 0), 0)
    return {
      code: voice,
      label: VOCI_BOLLETTA[voice] ?? voice,
      total,
      color: COLORS_PIE[voice] ?? 'hsl(var(--primary))',
    }
  })
}

export function DetailPeriod({ periodLabel, period, totaleBolletta }: Props) {
  if (!period || typeof period !== 'object') {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Dettaglio non disponibile per questa bolletta.
        </p>
      </Card>
    )
  }

  const voiceTotals = buildVoiceTotals(period)
  const totalePeriodo = voiceTotals.reduce((acc, v) => acc + v.total, 0)
  const voiceCodes = Object.keys(period)

  const barsItems: HorizontalBarItem[] = voiceTotals.map((v) => ({
    label: v.code,
    value: v.total,
    color: v.color,
  }))

  return (
    <div className="flex flex-col gap-4">
      {/* Riepilogo voci */}
      <Card className="p-5">
        <h2 className="mb-4 text-base font-bold text-foreground">
          Riepilogo voci · {periodLabel}
        </h2>
        <ul>
          {voiceTotals.map((v, idx) => (
            <li
              key={v.code}
              className={cn(
                'flex items-center gap-3 py-2.5',
                idx < voiceTotals.length - 1 && 'border-b border-border',
              )}
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: v.color }}
              />
              <span className="flex-1 text-sm text-foreground">{v.label}</span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {v.total.toFixed(2).replace('.', ',')} {EURO}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex items-center justify-between border-t border-border pt-4">
          <span className="text-base font-bold text-foreground">Totale periodo</span>
          <span className="text-base font-bold tabular-nums text-secondary">
            {totalePeriodo.toFixed(2).replace('.', ',')} {EURO}
          </span>
        </div>
      </Card>

      {/* Totale bolletta (highlight) */}
      <Card className="bg-muted/40 p-5">
        <div className="flex items-center justify-between">
          <span className="text-base font-bold text-foreground">Totale bolletta</span>
          <span className="text-lg font-bold tabular-nums text-secondary">
            {Number(totaleBolletta || 0).toFixed(2).replace('.', ',')} {EURO}
          </span>
        </div>
      </Card>

      {/* Distribuzione voci */}
      <Card className="p-5">
        <h3 className="mb-4 text-sm font-semibold text-foreground">
          Distribuzione voci
        </h3>
        <HorizontalBarsChart items={barsItems} unit={EURO} />
      </Card>

      {/* Dettaglio per voce */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-bold text-foreground">Dettaglio per voce</h2>
        <div className="flex flex-col gap-3">
          {voiceCodes.map((voice) => (
            <VoiceTable
              key={voice}
              voice={voice}
              items={Array.isArray(period[voice]) ? period[voice] : []}
            />
          ))}
        </div>
      </section>
    </div>
  )
}
